import { useQuery } from '@tanstack/react-query';
import { API_BASE_URL, apiClient } from '@/services/api-client';
import { dbClient, normalizeRpcResponse } from '@/services/db-client';
import {
    CAPTURE_DATA_MAX,
    CAPTURE_EXPORT_FETCH_SIZE,
    normalizeCaptureRow,
    normalizeFacets,
} from './captureExportModel';
import type { CaptureExportFacets, CaptureExportPage, CaptureExportRow } from './types';

type RpcFilters = Record<string, string | number | boolean>;

export async function fetchCaptureExportPage(
    filters: RpcFilters,
    page: number,
    pageSize: number,
): Promise<CaptureExportPage> {
    const { data, error } = await dbClient.rpc('admin_list_captures_for_export', {
        p_filters: filters,
        p_page: page,
        p_page_size: pageSize,
    });
    if (error) throw new Error(error.message);
    const raw = normalizeRpcResponse(data) as Record<string, unknown>[];
    return {
        rows: raw.map(normalizeCaptureRow),
        totalCount: Number(raw[0]?.total_count ?? 0),
    };
}

/** Todas las capturas del filtro hasta el tope de exportación de datos. */
export async function fetchCapturesForExport(
    filters: RpcFilters,
    onProgress?: (loaded: number, total: number) => void,
): Promise<{ rows: CaptureExportRow[]; truncated: boolean }> {
    const rows: CaptureExportRow[] = [];
    let total = 0;
    for (let page = 1; rows.length < CAPTURE_DATA_MAX; page += 1) {
        const result = await fetchCaptureExportPage(filters, page, CAPTURE_EXPORT_FETCH_SIZE);
        total = result.totalCount;
        rows.push(...result.rows);
        onProgress?.(rows.length, Math.min(total, CAPTURE_DATA_MAX));
        if (result.rows.length < CAPTURE_EXPORT_FETCH_SIZE || rows.length >= total) break;
    }
    return { rows: rows.slice(0, CAPTURE_DATA_MAX), truncated: total > CAPTURE_DATA_MAX };
}

export async function fetchCapturePhoto(captureId: string, mediaId: string | null): Promise<Uint8Array> {
    const token = await apiClient.getValidToken();
    if (!token) throw new Error('Sesión no válida');
    const params = new URLSearchParams({ captureId });
    if (mediaId) params.set('mediaId', mediaId);
    const res = await fetch(`${API_BASE_URL}/ops/capture-export/media?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
        let message = `HTTP ${res.status}`;
        try {
            const body = (await res.json()) as { error?: string; message?: string };
            message = body.error || body.message || message;
        } catch {
            /* cuerpo no JSON */
        }
        throw new Error(message);
    }
    return new Uint8Array(await res.arrayBuffer());
}

export function useCaptureExportList(filters: RpcFilters, page: number, pageSize: number) {
    return useQuery<CaptureExportPage>({
        queryKey: ['admin', 'capture-export', filters, page, pageSize],
        queryFn: () => fetchCaptureExportPage(filters, page, pageSize),
        staleTime: 60_000,
        placeholderData: (prev) => prev,
    });
}

export function useCaptureExportFacets() {
    return useQuery<CaptureExportFacets>({
        queryKey: ['admin', 'capture-export-facets'],
        queryFn: async () => {
            const { data, error } = await dbClient.rpc('admin_capture_export_facets');
            if (error) throw new Error(error.message);
            return normalizeFacets(data);
        },
        staleTime: 10 * 60_000,
    });
}
